/**
 * Cal.com API Integration
 *
 * This module provides Cal.com API integration for fetching bookings.
 *
 * Setup Instructions:
 * 1. Go to Cal.com Settings > Developer > API Keys
 * 2. Create a new API key
 * 3. Add to Vercel environment variables (server-side, no REACT_APP_ prefix):
 *    - CALCOM_API_KEY=your_api_key_here
 *
 * API Documentation: https://cal.com/docs/api-reference/v1
 */

import { getAuthHeaders } from 'src/utils/auth-headers';

// Always use proxy - API key is handled server-side
const CALCOM_API_BASE = '/api/proxy/calcom';

// Types for Cal.com API responses
export interface CalcomBooking {
  id: number;
  uid: string;
  title: string;
  description: string | null;
  startTime: string;
  endTime: string;
  status: 'ACCEPTED' | 'PENDING' | 'CANCELLED' | 'REJECTED';
  attendees: CalcomAttendee[];
  user: {
    id: number;
    name: string;
    email: string;
  } | null;
  eventType: {
    id: number;
    title: string;
    slug: string;
    length: number;
  } | null;
  location: string | null;
  metadata: Record<string, any> | null;
  createdAt: string;
  updatedAt: string;
}

export interface CalcomAttendee {
  id: number;
  email: string;
  name: string;
  timeZone: string;
  locale: string | null;
}

export interface CalcomBookingsResponse {
  bookings: CalcomBooking[];
}

export interface CalcomEventType {
  id: number;
  title: string;
  slug: string;
  description: string | null;
  length: number;
  hidden: boolean;
  position: number;
  locations: any[];
  metadata: Record<string, any> | null;
}

export interface CalcomEventTypesResponse {
  event_types: CalcomEventType[];
}

// API Functions

/**
 * Check if Cal.com API is configured
 * Always returns true since the API key is handled server-side
 */
export const isCalcomConfigured = (): boolean => {
  return true; // API key is validated server-side
};

/**
 * Fetch all bookings from Cal.com
 * Note: Cal.com API v1 only accepts 'upcoming' or 'cancelled' as status values.
 * For 'past' bookings, we fetch all and filter client-side.
 */
export const getBookings = async (params?: {
  status?: 'upcoming' | 'past' | 'cancelled' | 'all';
  dateFrom?: string;
  dateTo?: string;
}): Promise<CalcomBooking[]> => {
  // Build query params for proxy
  const queryParams = new URLSearchParams();
  queryParams.append('endpoint', '/bookings');

  // Cal.com API v1 only accepts 'upcoming' as a valid status filter
  // For 'past', 'cancelled', or 'all', we fetch all and filter client-side
  if (params?.status === 'upcoming') {
    queryParams.append('status', 'upcoming');
  }
  if (params?.dateFrom) {
    queryParams.append('dateFrom', params.dateFrom);
  }
  if (params?.dateTo) {
    queryParams.append('dateTo', params.dateTo);
  }

  const response = await fetch(`${CALCOM_API_BASE}?${queryParams.toString()}`, {
    headers: {
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || `Failed to fetch bookings: ${response.status}`);
  }

  const data: CalcomBookingsResponse = await response.json();
  let bookings = data.bookings || [];

  // Client-side filter for statuses that Cal.com API doesn't support as filter params
  if (params?.status === 'past') {
    const now = new Date();
    bookings = bookings.filter((booking) => new Date(booking.endTime) < now);
  } else if (params?.status === 'cancelled') {
    bookings = bookings.filter((booking) => booking.status === 'CANCELLED');
  } else if (params?.status === 'upcoming') {
    // Filter out cancelled bookings from upcoming - API may still return them
    bookings = bookings.filter((booking) => booking.status !== 'CANCELLED');
  }

  return bookings;
};

/**
 * Fetch a single booking by ID
 */
export const getBooking = async (bookingId: number): Promise<CalcomBooking> => {
  const queryParams = new URLSearchParams();
  queryParams.append('endpoint', `/bookings/${bookingId}`);

  const response = await fetch(`${CALCOM_API_BASE}?${queryParams.toString()}`, {
    headers: {
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || `Failed to fetch booking: ${response.status}`);
  }

  const data = await response.json();
  return data.booking;
};

/**
 * Cancel a booking
 * Note: Cal.com API v1 uses DELETE to /bookings/{id}/cancel
 */
export const cancelBooking = async (bookingId: number, reason?: string): Promise<void> => {
  const queryParams = new URLSearchParams();
  queryParams.append('endpoint', `/bookings/${bookingId}/cancel`);

  const response = await fetch(`${CALCOM_API_BASE}?${queryParams.toString()}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ reason: reason || 'Cancelled by user' }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || `Failed to cancel booking: ${response.status}`);
  }
};

/**
 * Create a new booking on Cal.com
 */
export interface CreateBookingParams {
  eventTypeId: number;
  start: string; // ISO date string
  end: string; // ISO date string
  name: string;
  email: string;
  phone?: string;
  location?: string;
  notes?: string;
  timeZone?: string;
}

export const createBooking = async (params: CreateBookingParams): Promise<CalcomBooking> => {
  const queryParams = new URLSearchParams();
  queryParams.append('endpoint', '/bookings');

  const response = await fetch(`${CALCOM_API_BASE}?${queryParams.toString()}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({
      eventTypeId: params.eventTypeId,
      start: params.start,
      end: params.end,
      responses: {
        name: params.name,
        email: params.email,
        phone: params.phone || '',
        location: params.location ? { value: params.location, optionValue: '' } : undefined,
        notes: params.notes || '',
      },
      timeZone: params.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: 'en',
      metadata: {},
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || `Failed to create booking: ${response.status}`);
  }

  const data = await response.json();
  return data;
};

/**
 * Fetch all event types
 */
export const getEventTypes = async (): Promise<CalcomEventType[]> => {
  const queryParams = new URLSearchParams();
  queryParams.append('endpoint', '/event-types');

  const response = await fetch(`${CALCOM_API_BASE}?${queryParams.toString()}`, {
    headers: {
      ...getAuthHeaders(),
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(error.message || `Failed to fetch event types: ${response.status}`);
  }

  const data: CalcomEventTypesResponse = await response.json();
  return data.event_types || [];
};

/**
 * Format booking for display
 */
export const formatBookingForDisplay = (booking: CalcomBooking) => {
  const startDate = new Date(booking.startTime);
  const endDate = new Date(booking.endTime);

  const attendee = booking.attendees?.[0];

  return {
    id: booking.id,
    uid: booking.uid,
    title: booking.title,
    description: booking.description,
    date: startDate.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }),
    time: `${startDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })} - ${endDate.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })}`,
    startTime: booking.startTime,
    endTime: booking.endTime,
    status: booking.status,
    attendeeName: attendee?.name || 'Unknown',
    attendeeEmail: attendee?.email || '',
    location: booking.location,
    eventType: booking.eventType?.title || 'Appointment',
    duration: booking.eventType?.length || 30,
  };
};

/**
 * Get status color for booking
 */
export const getBookingStatusColor = (status: CalcomBooking['status']): string => {
  switch (status) {
    case 'ACCEPTED':
      return '#10B981'; // Green
    case 'PENDING':
      return '#F59E0B'; // Amber
    case 'CANCELLED':
      return '#EF4444'; // Red
    case 'REJECTED':
      return '#6B7280'; // Gray
    default:
      return '#6B7280';
  }
};

/**
 * Get status label for booking
 */
export const getBookingStatusLabel = (status: CalcomBooking['status']): string => {
  switch (status) {
    case 'ACCEPTED':
      return 'Confirmed';
    case 'PENDING':
      return 'Pending';
    case 'CANCELLED':
      return 'Cancelled';
    case 'REJECTED':
      return 'Rejected';
    default:
      return status;
  }
};
